/**
 * ReportCitationButton — inline citation pill that opens the source URL.
 */

export const sanitizeCiteTitle = (title, url, idx) => {
  if (title) {
    const cleaned = title.replace(/[^\x20-\x7E\u00C0-\u024F]/g, "").trim();
    const alphaCount = [...cleaned].filter((c) =>
      /[a-zA-Z\u00C0-\u024F]/.test(c),
    ).length;
    if (cleaned.length >= 3 && alphaCount >= cleaned.length * 0.3)
      return cleaned.length > 30 ? cleaned.slice(0, 29) + "\u2026" : cleaned;
  }
  if (url) {
    try {
      const domain = new URL(url).hostname.replace(/^www\./, "");
      if (domain)
        return domain.length > 30 ? domain.slice(0, 29) + "\u2026" : domain;
    } catch {
      /* ignore */
    }
  }
  return `Source ${idx}`;
};

const ReportCitationButton = ({
  index,
  citation,
  hoveredRef,
  onHoverIn,
  onHoverOut,
  onSourceClick,
  activeSourceId,
  citationInteractions,
}) => {
  const handleClick = () => {
    if (onSourceClick) {
      onSourceClick(citation);
    } else if (citation.url) {
      window.open(citation.url, "_blank", "noopener,noreferrer");
    }
  };
  const label = sanitizeCiteTitle(citation.title, citation.url, index);
  const isHighlighted = hoveredRef?.current === index;
  const citationKey = citation.doc_id || citation.url;
  const isActive = activeSourceId && activeSourceId === citationKey;
  const interactions = citationInteractions?.[citationKey];

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => onHoverIn?.(index)}
      onMouseLeave={() => onHoverOut?.()}
      title={citation.title || citation.url || `Source ${index}`}
      data-report-citation="true"
      data-export-label={index > 0 ? `[${index}]` : label}
      className={[
        "inline-flex items-center justify-center gap-0.5 h-[18px] px-1.5 mx-[1px] rounded",
        "text-[10px] font-medium leading-none cursor-pointer",
        "transition-all duration-150 align-middle whitespace-nowrap",
        isActive || isHighlighted
          ? "bg-sky-300 text-sky-900 ring-1 ring-sky-400 dark:bg-sky-600/60 dark:text-sky-100 dark:ring-sky-500"
          : "bg-sky-100 text-sky-700 hover:bg-sky-200 dark:bg-sky-900/40 dark:text-sky-300 dark:hover:bg-sky-800/50",
      ].join(" ")}
    >
      {label}
      {interactions &&
        (interactions.like || interactions.dislike || interactions.catalog) && (
          <span className="inline-flex items-center gap-px ml-0.5">
            {interactions.like && (
              <span className="inline-block w-[7px] h-[7px] rounded-full bg-emerald-400" />
            )}
            {interactions.dislike && (
              <span className="inline-block w-[7px] h-[7px] rounded-full bg-red-400" />
            )}
            {interactions.catalog && (
              <span className="inline-block w-[7px] h-[7px] rounded-full bg-amber-400" />
            )}
          </span>
        )}
    </button>
  );
};

export default ReportCitationButton;
